import { AgentChat } from '@cadstrom/cadstrom-shared';
import { Activity, Suspense, useMemo } from 'react';

import { Loading } from '#components/loading.js';
import {
	AddAgentWorkbenchPageContext,
	AgentWorkbenchPageContextProvider,
} from '#screens/agent-workbench/agent-workbench-page-context.js';
import { AgentWorkbenchPanel } from '#screens/agent-workbench/agent-workbench-panel.js';
import { AgentWorkbenchRuntimeProvider } from '#screens/agent-workbench/agent-workbench-runtime-provider.js';
import { buildAgentWorkbenchThreadListAdapter } from '#screens/agent-workbench/agent-workbench-thread-list-adapter.js';
import { GenericErrorBoundary } from '#screens/error-boundaries/generic-error-boundary.js';
import { useTRPCClient } from '#src/trpc.js';

import type { AgentWorkbenchAnalyticsContext } from '#screens/agent-workbench/agent-workbench-analytics.js';

/** Workbench events from this surface; the part in view rides along from the page context. */
const DATASHEET_WORKBENCH_ANALYTICS: AgentWorkbenchAnalyticsContext = { source: 'datasheet_chat' };

export interface DatasheetAgentWorkbenchProps {
	/** The datasheet the conversation is about, and what the agent is told about it. */
	datasheet: {
		fileId: string;
		/** The file's name as shown to the user, when the viewer has resolved it. */
		fileName?: string | undefined;
		/** Where the file came from, when known. */
		sourceUrl?: string | null | undefined;
		mpn?: string | undefined;
		manufacturerName?: string | null | undefined;
		/** Which part in the customer's design this datasheet was reached from, when known. */
		referenceDesignator?: string | undefined;
	};
	/** Scopes the conversation list to a project, when the surface knows one. */
	projectId?: string | undefined;
	/** Scopes the conversation to the package this datasheet belongs to, when there is one. */
	packageId?: string | undefined;
	/**
	 * Take the panel off screen without discarding it. The conversation, any run in flight, and the panel's own
	 * state all survive, so it comes back as the user left it.
	 *
	 * @default false
	 */
	isHidden?: boolean;
}

/**
 * The agent workbench for a datasheet: the conversation list, the live runtime and the panel, with the
 * part under discussion registered as page context so the agent knows which datasheet is on screen.
 *
 * @param props - {@link DatasheetAgentWorkbenchProps}.
 * @returns The workbench surface, bound to its own conversation thread.
 */
export function DatasheetAgentWorkbench({
	datasheet,
	projectId,
	packageId,
	isHidden = false,
}: DatasheetAgentWorkbenchProps) {
	const trpcClient = useTRPCClient();
	const { adapter, listReady } = useMemo(
		() =>
			buildAgentWorkbenchThreadListAdapter({
				agentId: AgentChat.CHAT_SURFACE_AGENT_IDS.datasheet,
				projectId,
				packageId,
				trpcClient,
			}),
		[projectId, packageId, trpcClient],
	);

	// Scoped to this workbench so the datasheet's context reaches its own agent and no other workbench the
	// surrounding page happens to host.
	return (
		<AgentWorkbenchPageContextProvider>
			<AddAgentWorkbenchPageContext
				label='Datasheet'
				context={{
					'Datasheet document file ID': datasheet.fileId,
					'Datasheet file name': datasheet.fileName,
					'Datasheet source URL': datasheet.sourceUrl ?? undefined,
					'Part (MPN)': datasheet.mpn,
					Manufacturer: datasheet.manufacturerName ?? undefined,
					'Reference designator': datasheet.referenceDesignator,
					'Project ID': projectId,
				}}
				// The reference designator is left out: it names a part in the customer's design.
				analytics={{
					package_mpn: datasheet.mpn,
					manufacturer: datasheet.manufacturerName ?? undefined,
					project_id: projectId,
				}}
			/>

			<GenericErrorBoundary>
				<Suspense fallback={<Loading />}>
					<AgentWorkbenchRuntimeProvider
						adapter={adapter}
						listReady={listReady}
						analytics={DATASHEET_WORKBENCH_ANALYTICS}
					>
						{/* Only the panel goes inside the boundary: the runtime driving the conversation must keep
						    running while it is hidden. */}
						<Activity mode={isHidden ? 'hidden' : 'visible'}>
							<AgentWorkbenchPanel placeholder='Ask about this part…' />
						</Activity>
					</AgentWorkbenchRuntimeProvider>
				</Suspense>
			</GenericErrorBoundary>
		</AgentWorkbenchPageContextProvider>
	);
}
